import { create } from 'zustand';

// When using video within the application, we will be presenting many videos in
// many different contexts. Moreover, as the user nagivates between contexts, they
// will want the application to be aligned with their intentions regarding which
// video should be playing and at what volume.

// To accomplish this, we'll need to:
// 1. add some event monitoring to suppliment video state information
// 2. define the global state model for video.
// 3. update, store, and retrieve the state across sessions.

// 1. add some event monitoring to suppliment video state information
// We'll define a map of events to set a data-property on the video element
// this will be used to inform the state model as the video elements change,
// because the native properties are not refined enough to detect the state.

// 1.1. Network Conditions
// Right now, only Chrome supports this standard, but we can use it to detect the
// network conditions and data-preferences of the user. When it is supported, we can
// use it to intelligently adjust the video quality. Without it, we'll just default
// to high-def with a fallback to standard-def.

declare global {
	interface Navigator {
		connection?: {
			effectiveType?: 'slow-2g' | '2g' | '3g' | '4g';
			saveData?: boolean;
			addEventListener: (type: string, listener: () => void) => void;
			removeEventListener: (type: string, listener: () => void) => void;
		};
	}
}

type NetworkConditionsState = {
	hdCapable: boolean;
	setHdCapable: (capable: boolean) => void;
};

export const useNetworkConditionsStore = create<NetworkConditionsState>((set) => ({
	hdCapable: true,
	setHdCapable: (capable) => set({ hdCapable: capable }),
}));

// Initialize network conditions monitoring
const updateNetworkConditions = () => {
	const connection = navigator.connection;
	const connectionSpeed = connection?.effectiveType || '4g';
	const isDataSaver = Boolean(connection?.saveData);

	useNetworkConditionsStore.getState().setHdCapable(!isDataSaver && connectionSpeed === '4g');
};

// Set up event listeners
if (navigator.connection) {
	navigator.connection.addEventListener('change', updateNetworkConditions);
	updateNetworkConditions();
}
